import { useCallback } from "react"
import { Box, Flex, Image, Text } from "@chakra-ui/react"
import { SupportedTokenName } from "@mezo/sdk"
import { Mats } from "@mezo-org/mezo-clay"

import alertIcon from "#/assets/circle-alert.svg"
import {
  RootState,
  selectActiveToken,
  selectTokenDecimals,
  useDappSelector,
} from "#/redux-state"
import CurrencySymbol from "#/shared/components/CurrencySymbol"
import DepositInput from "#/shared/components/DepositInput"
import {
  BASE_DAILY_MATS,
  BITCOIN_SEPOLIA_MINIMUM_DEPOSIT_TO_WITHDRAW,
  SDK_NETWORK,
} from "#/shared/constants"
import {
  isStablecoin,
  isStbtcToken,
  SPECIAL_MULTIPLIER_STABLECOINS,
} from "#/shared/utils"

import DepositToggleWallet from "./DepositToggleWallet"

function BaseDailyMats({ activeToken }: { activeToken: SupportedTokenName }) {
  const specialCase = SPECIAL_MULTIPLIER_STABLECOINS[activeToken]

  const getMatsDisplay = useCallback(
    (mats: number) => (
      <Flex
        display="inline-flex"
        alignItems="center"
        gap="2px"
        padding="4px 8px"
        background="#DEFF80"
        rounded="40px"
      >
        <Text
          fontSize="14px"
          fontWeight={700}
          fontFamily="riforma.bold"
          lineHeight="16px"
        >
          {mats}
        </Text>
        <Mats size={16} />
        <Text
          fontSize="14px"
          fontWeight={700}
          fontFamily="riforma.bold"
          lineHeight="16px"
        >
          per day
        </Text>
      </Flex>
    ),
    [],
  )

  if (specialCase) {
    return (
      <Box mt={1}>
        <Text variant="label" as="span">
          40k $<CurrencySymbol token={activeToken} /> ={" "}
        </Text>
        {getMatsDisplay(specialCase.dailyMats)}
      </Box>
    )
  }

  if (isStablecoin(activeToken)) {
    return (
      <Box mt={1}>
        <Text variant="label" as="span">
          40k $<CurrencySymbol token={activeToken} /> ={" "}
        </Text>
        {getMatsDisplay(BASE_DAILY_MATS.stablecoin)}
      </Box>
    )
  }

  return (
    <Box mt={1}>
      <Text variant="label" as="span">
        1 <CurrencySymbol token={activeToken} /> ={" "}
      </Text>
      {getMatsDisplay(
        isStbtcToken(activeToken)
          ? BASE_DAILY_MATS.stbtc
          : BASE_DAILY_MATS.default,
      )}
    </Box>
  )
}

export default function DepositInputWrapper() {
  const activeToken = useDappSelector(selectActiveToken)

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, activeToken),
  )

  return (
    <>
      <Flex
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Text
          variant="body"
          fontWeight={500}
          fontFamily="riforma.medium"
          color="primary.black.70"
        >
          Amount
        </Text>
        <BaseDailyMats activeToken={activeToken} />
      </Flex>
      <Flex w="100%" gap={2}>
        <Box position="relative" w="391px" mb={3} flexGrow={1}>
          <DepositInput placeholder="0" precisionAmount={tokenDecimals} />
        </Box>
        <DepositToggleWallet />
      </Flex>
      <Flex mb={5} gap={1} alignItems="center" h="24px">
        {SDK_NETWORK === "sepolia" && activeToken === "BTC" && (
          <>
            <Image src={alertIcon} w="24px" />
            <Text variant="label">
              Testnet only: deposit at least{" "}
              {BITCOIN_SEPOLIA_MINIMUM_DEPOSIT_TO_WITHDRAW}{" "}
              <CurrencySymbol token="BTC" /> to make the deposit withdrawable
            </Text>
          </>
        )}
      </Flex>
    </>
  )
}
